import { inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { pipe, switchMap, tap } from 'rxjs';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  SchoolAccessSettingsRequest,
  SchoolAccessSettingsState,
  initialSchoolAccessSettingsState,
} from '../entities/school-access-settings.model';
import { SchoolAccessSettingsService } from '../infra/school-access-settings.service';

const OPS = ['load', 'update'] as const;

/**
 * Per-tenant school access-control flags (SchoolService `/AccessSettings`).
 * A single record: `load` reads the tenant's flags and `update` patches them
 * (only the provided fields change). This is stored config only — the access
 * edge enforces the rules; the store never gates entry itself.
 */
export const SchoolAccessSettingsStore = signalStore(
  { providedIn: 'root' },
  withState<SchoolAccessSettingsState>(initialSchoolAccessSettingsState),
  withReqState(...OPS),
  withMethods(
    (
      store,
      service = inject(SchoolAccessSettingsService),
      errorStore = inject(ErrorStore),
    ) => ({
      load: rxMethod<void>(
        pipe(
          tap(() => patchState(store, setPropLoading('load'))),
          switchMap(() =>
            service.get().pipe(
              tapResponse({
                next: (accessSettings) =>
                  patchState(store, { accessSettings }, setPropLoaded('load')),
                error: (e: HttpErrorResponse) => patchState(store, setPropError('load', e)),
              }),
            ),
          ),
        ),
      ),

      update: rxMethod<SchoolAccessSettingsRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('update'))),
          switchMap((body) =>
            service.update(body).pipe(
              tapResponse({
                next: (accessSettings) =>
                  patchState(store, { accessSettings }, setPropLoaded('update')),
                error: (e: HttpErrorResponse) => {
                  patchState(store, setPropError('update', e));
                  errorStore.setHttpError(e);
                },
              }),
            ),
          ),
        ),
      ),

      reset(): void {
        patchState(store, initialSchoolAccessSettingsState, setPropInit(...OPS));
      },
    }),
  ),
);
